import { memo, useState, FC } from 'react';
import { useFormik } from 'formik';
import { isEmpty, noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Validation
import { getSMSTestSendValidationSchema } from '../../validation';
// Import Hooks
import useToaster from 'hooks/UseToaster';
// Import UI components
import { ExtendedInput, Modal, Button, Icon, Label } from '@geneui/components';
// Import Components
import ButtonWithLoader from 'components/common/ButtonWithLoader';
// Import Constants
import { AlertTypes, l } from 'constants/common';
import { SMS_TEST_SEND_DEFAULT_VALUES, SMSTestSendFieldsEnum } from '../../constants';

const { success } = AlertTypes;

interface FormFields {
    [SMSTestSendFieldsEnum.MobilePhone]: string;
}

interface Props {
    handleTestSend: (values: FormFields) => Promise<string>;
    handleClose: () => void;
}

const SMSTestSendModal: FC<Props> = ({ handleTestSend, handleClose }) => {
    const { t } = useTranslation();
    const { showToaster } = useToaster();

    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const formik = useFormik<FormFields>({
        initialValues: SMS_TEST_SEND_DEFAULT_VALUES,
        validationSchema: getSMSTestSendValidationSchema(t),
        onSubmit: noop,
    });

    const { values, errors, touched, handleChange, handleBlur, submitForm, validateForm } = formik;

    const handleTestSendClick = () => {
        submitForm().then(() =>
            validateForm(values).then((formErrors) => {
                if (isEmpty(formErrors)) {
                    setIsButtonLoading(true);

                    handleTestSend(values)
                        .then((alertMessage) => {
                            showToaster(success, alertMessage);
                            handleClose();
                        })
                        .catch(noop)
                        .finally(() => {
                            setIsButtonLoading(false);
                        });
                }
            }),
        );
    };

    return (
        <>
            <Modal
                withPortal={true}
                closeOnClickOutside={true}
                onCancel={handleClose}
                background="dark-background"
                position="top"
                className="test-template-modal"
                visible={true}
                closable={false}
                size="content-size"
                title={t(l.SMSProviderTest)}
                footer={
                    <>
                        <Button flexibility="default" appearance="minimal" onClick={handleClose}>
                            {t(l.Close)}
                        </Button>
                        <ButtonWithLoader
                            flexibility="default"
                            color="primary"
                            onClick={handleTestSendClick}
                            isLoading={isButtonLoading}
                            isDisabled={isButtonLoading}
                        >
                            {t(l.SendNow)}
                        </ButtonWithLoader>
                    </>
                }
            >
                <div className="text-icon-block">
                    <Icon type="bc-icon-test-48" />
                    <Label className={'test-title-txt'}>{t(l.YouWillReceiveCRMTestMessage)}</Label>
                </div>

                <Label className={'test-txt'}>{t(l.SendTo)}</Label>

                <ExtendedInput
                    name={SMSTestSendFieldsEnum.MobilePhone}
                    placeholder={t(l.PhoneNumber)}
                    label={t(l.PhoneNumber)}
                    labelAppearance="swap"
                    appearance="outline"
                    inputSize="big"
                    value={values[SMSTestSendFieldsEnum.MobilePhone]}
                    errorText={touched[SMSTestSendFieldsEnum.MobilePhone] && errors[SMSTestSendFieldsEnum.MobilePhone]}
                    isValid={!(touched[SMSTestSendFieldsEnum.MobilePhone] && errors[SMSTestSendFieldsEnum.MobilePhone])}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            </Modal>
        </>
    );
};

export default memo(SMSTestSendModal);
